import type {
  ClinicalSeverityData,
  GateResult,
  ProcedureContext,
  TargetAnatomy,
  UniversalPayerFlags,
} from '@/lib/clinicalSeverityTypes';
import { buildDynamicGaps } from './rules';
import {
  derivePatientDemographics,
  getRequiredPTWeeks,
  inferAnatomyFromCPT,
} from './utils';

export interface OrthopedicCaseEvaluation {
  payerName: string;
  targetAnatomy: TargetAnatomy;
  primaryCpt: string;
  laterality: ProcedureContext['laterality'];
  icd10Codes: string[];
  payerFlags: UniversalPayerFlags;
  exemptionActive: boolean;
  biomarker: {
    required: boolean;
    diabetic: boolean;
    hba1c: number | null;
    passed: boolean;
    reason: string;
  };
  gateSummary: {
    gate1ExemptionActive: boolean;
    gate2CriticalError: boolean;
    gate3ThresholdSatisfied: boolean;
    gate4ContextSatisfied: boolean;
  };
  gateResults: GateResult[];
  requiredPTWeeks: number;
  resolvedGaps: ReturnType<typeof buildDynamicGaps>;
  unresolvedGaps: ReturnType<typeof buildDynamicGaps>;
  status: 'resolved' | 'unresolved' | 'critical_error';
  summary: string[];
}

const DEFAULT_PAYER_FLAGS: UniversalPayerFlags = {
  bone_on_bone_exemption: false,
  acute_fracture_avulsion: false,
  neurologic_deficit: false,
  extended_therapy_met: false,
};

const TOTAL_JOINT_CPTS = new Set(['27447', '27446', '27130', '27132', '23472', '23470']);
const THRESHOLD_RULE_IDS = new Set(['gap-pt-duration']);
const CONTEXTUAL_RULE_IDS = new Set([
  'gap-imaging',
  'gap-injection',
  'gap-shoulder-mri',
  'gap-spine-neuro',
  'gap-xray',
]);

function resolveTargetAnatomy(
  activeCptCodes: string[],
  clinicalData: ClinicalSeverityData | null | undefined,
): TargetAnatomy {
  const explicitAnatomy = clinicalData?.procedure_context?.target_anatomy;
  if (explicitAnatomy && explicitAnatomy !== 'unspecified') return explicitAnatomy;

  const primaryCpt = clinicalData?.procedure_context?.primary_cpt || activeCptCodes[0] || '';
  return primaryCpt ? inferAnatomyFromCPT(primaryCpt) : 'unspecified';
}

function buildExemptionReason(payerFlags: UniversalPayerFlags): string {
  const reasons: string[] = [];

  if (payerFlags.bone_on_bone_exemption) reasons.push('bone-on-bone mechanical destruction');
  if (payerFlags.acute_fracture_avulsion) reasons.push('acute fracture/avulsion pathology');
  if (payerFlags.neurologic_deficit) reasons.push('documented neurologic deficit');

  return reasons.join('; ');
}

export function evaluateOrthopedicCase(
  activeCptCodes: string[],
  payerName: string,
  clinicalData: ClinicalSeverityData | null | undefined,
): OrthopedicCaseEvaluation {
  const primaryCpt = clinicalData?.procedure_context?.primary_cpt || activeCptCodes[0] || '';
  const laterality = clinicalData?.procedure_context?.laterality || 'n/a';
  const icd10Codes = clinicalData?.procedure_context?.icd_10_codes || [];
  const targetAnatomy = resolveTargetAnatomy(activeCptCodes, clinicalData);
  const patient = clinicalData
    ? derivePatientDemographics(clinicalData)
    : { age: 65, bmi: 28, diabetic: false, hba1c: undefined };
  const payerFlags: UniversalPayerFlags = {
    ...DEFAULT_PAYER_FLAGS,
    ...(clinicalData?.payer_risk_flags || {}),
  };

  const exemptionReason = buildExemptionReason(payerFlags);
  const exemptionActive = Boolean(exemptionReason);
  const requiredPTWeeks = getRequiredPTWeeks(patient, targetAnatomy);
  const resolvedGaps = buildDynamicGaps(activeCptCodes, payerName, clinicalData);
  const unresolvedGaps = resolvedGaps.filter((gap) => gap.aiResolvedValue === null);

  const codingErrorGaps = resolvedGaps.filter((gap) => gap.id.startsWith('coding-error-'));

  const biomarkerRequired = Boolean(patient.diabetic && TOTAL_JOINT_CPTS.has(primaryCpt));
  const hba1c = clinicalData?.hba1c ?? null;
  let biomarkerPassed = true;
  let biomarkerReason = 'No HbA1c biomarker requirement triggered.';

  if (biomarkerRequired) {
    if (hba1c === null) {
      biomarkerPassed = false;
      biomarkerReason = 'HbA1c is required for diabetic patients undergoing total joint replacement, but the value is missing.';
    } else if (hba1c >= 8.0) {
      biomarkerPassed = false;
      biomarkerReason = `HbA1c ${hba1c} is non-compliant for diabetic total joint authorization (must be < 8.0).`;
    } else {
      biomarkerReason = `HbA1c ${hba1c} is documented and compliant for diabetic total joint authorization.`;
    }
  }

  const thresholdUnresolvedGaps = unresolvedGaps.filter((gap) => THRESHOLD_RULE_IDS.has(gap.id));
  const contextualUnresolvedGaps = unresolvedGaps.filter((gap) => CONTEXTUAL_RULE_IDS.has(gap.id));

  const gate2CriticalError = codingErrorGaps.length > 0 || !biomarkerPassed;
  const gate3ThresholdSatisfied = exemptionActive || thresholdUnresolvedGaps.length === 0;
  const gate4ContextSatisfied = contextualUnresolvedGaps.length === 0;

  const gateResults: GateResult[] = [
    {
      gate: 'exemption',
      passed: exemptionActive,
      reason: exemptionActive
        ? `Acute clinical exemption active: ${exemptionReason}. Conservative therapy prerequisites may be bypassed.`
        : 'No acute exemption override is active.',
    },
    {
      gate: 'coding',
      passed: !gate2CriticalError,
      reason: codingErrorGaps.length > 0
        ? codingErrorGaps.map((gap) => gap.question).join(' | ')
        : biomarkerReason,
    },
    {
      gate: 'threshold',
      passed: gate3ThresholdSatisfied,
      reason: exemptionActive
        ? `Conservative therapy threshold bypassed due to acute exemption: ${exemptionReason}.`
        : gate3ThresholdSatisfied
          ? `Conservative therapy meets the required ${requiredPTWeeks}-week threshold.`
          : `Conservative therapy does not meet the required ${requiredPTWeeks}-week threshold or requires manual verification.`,
    },
    {
      gate: 'contextual',
      passed: gate4ContextSatisfied,
      reason: gate4ContextSatisfied
        ? 'Imaging/injection findings are sufficiently contextualized or manually verified.'
        : contextualUnresolvedGaps.map((gap) => `${gap.payerRule}: manual verification required.`).join(' | '),
    },
  ];

  const status: OrthopedicCaseEvaluation['status'] = gate2CriticalError
    ? 'critical_error'
    : unresolvedGaps.length > 0
      ? 'unresolved'
      : 'resolved';

  const summary = [
    gateResults[0].reason,
    gateResults[1].reason,
    gateResults[2].reason,
    gateResults[3].reason,
  ];

  return {
    payerName,
    targetAnatomy,
    primaryCpt,
    laterality,
    icd10Codes,
    payerFlags,
    exemptionActive,
    biomarker: {
      required: biomarkerRequired,
      diabetic: Boolean(patient.diabetic),
      hba1c,
      passed: biomarkerPassed,
      reason: biomarkerReason,
    },
    gateSummary: {
      gate1ExemptionActive: exemptionActive,
      gate2CriticalError,
      gate3ThresholdSatisfied,
      gate4ContextSatisfied,
    },
    gateResults,
    requiredPTWeeks,
    resolvedGaps,
    unresolvedGaps,
    status,
    summary,
  };
}